import { styled } from '@mui/material';
import type React from 'react';
import { useCallback, useRef, useState } from 'react';
import Marquee from 'react-fast-marquee';

// some styles are in SelectableCard.tsx since they rely on the hover state of the card
const MarqueeTitle = styled('div')(
  () => `
  text-transform: capitalize;
  font-weight: 600;
  line-height: 20px;
  color: var(--color-text-neutral-primary);
  overflow: hidden;
  width: 100%;
  .rfm-child {
    width: 100%;
    white-space: nowrap;
    margin-right: 32px;
  }
`,
);

interface CustomHTMLAttributes<T> extends React.HTMLAttributes<T> {
  [key: `data-${string}`]: string | undefined;
}

interface ICardTitleProps {
  fontSize: number;
  children: React.ReactNode;
  textProps?: CustomHTMLAttributes<HTMLDivElement>;
  containerStyle?: React.CSSProperties;
}

export function CardTitle({ fontSize, children, textProps = {}, containerStyle }: ICardTitleProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const marqueeRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);
  const [hasFocus, setHasFocus] = useState(false);

  const canToggleScroll = useCallback(() => {
    if (titleRef.current && wrapperRef.current && !hasFocus) {
      const textWidth = titleRef.current.scrollWidth;
      const visibleWidth = wrapperRef.current.clientWidth;
      return textWidth > visibleWidth && setHasFocus(true);
    }
  }, [hasFocus]);

  const mouseLeave = useCallback(() => {
    setHasFocus(false);
    if (marqueeRef.current) {
      const marqueeContainer = marqueeRef.current as HTMLElement;
      const marqueeChildren = Array.from(marqueeContainer.children) as HTMLElement[];

      marqueeChildren.forEach((marquee: HTMLElement) => {
        marquee.style.animation = 'none';
        marquee.offsetHeight; // accessing the property is necessary to trigger the reflow
        marquee.style.animation = '';
      });
    }
  }, []);

  return (
    <MarqueeTitle
      ref={wrapperRef}
      role="heading"
      style={{ ...containerStyle, fontSize: `${fontSize}px` }}
      onMouseEnter={canToggleScroll}
      onMouseLeave={mouseLeave}
    >
      <Marquee ref={marqueeRef} play={hasFocus}>
        <div
          ref={titleRef}
          style={{
            textOverflow: 'ellipsis',
            overflow: 'hidden',
          }}
          {...textProps}
        >
          {children}
        </div>
      </Marquee>
    </MarqueeTitle>
  );
}
